import React, { Component } from 'react'
import { Redirect } from 'react-router-dom'
import {
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import { connect } from 'react-redux'
import { Box, Button, SwxTheme, Textfield } from 'swx-atomic-component-lib'

import { searchUsers, impersonateUser } from '../../actions/users'
import utils from '../../helpers/util.helper'
import i18n from '../../i18n'

import {
  UserListContainer,
  UserListItem,
  LoadMoreButton
} from './styled'

const styles = (theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing.unit * 3,
    overflowX: 'auto'
  },
  container: {
    backgroundColor: SwxTheme.palette.g100,
    padding: SwxTheme.sizing.x3
  },
  searchInput: {
    width: '420px',
    backgroundColor: SwxTheme.palette.white,
    color: SwxTheme.palette.black
  },
  selectedUser: {
    backgroundColor: SwxTheme.palette.primary
  },
  impersonateUserButton: {
    marginTop: SwxTheme.sizing.x1
  },
  searchWrapper: {
    position: 'relative'
  }
})

const UserList = ({
  classes,
  users,
  onUserSelect,
  selectedUser,
  isLoading,
  hasMore,
  onLoadMore
}) => {
  return (
    <UserListContainer>
      <List>
        {users.map((user) => (
          <UserListItem button key={user.id} onClick={() => onUserSelect(user)}>
            <ListItemText
              primary={`${user.first_name} ${user.last_name}`}
              secondary={user.email}
            />
          </UserListItem>
        ))}

        {isLoading && (
          <ListItem style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </ListItem>
        )}

        {hasMore && !isLoading && (
          <LoadMoreButton button onClick={onLoadMore}>
            <Typography>{i18n.impersonateUser.input.loadMore}</Typography>
          </LoadMoreButton>
        )}

        {users.length === 0 && !isLoading && (
          <ListItem>
            <ListItemText primary='No users found' />
          </ListItem>
        )}
      </List>
    </UserListContainer>
  )
}

class ImpersonateUser extends Component {
  state = {
    redirectTo: null,
    users: [],
    selectedUser: null,
    inputValue: '',
    isProcessing: false,
    hasMore: false,
    currentPage: 0,
    showList: false
  }

  constructor (props) {
    super(props)
    this.classes = props.classes
    this.searchWrapperRef = React.createRef()

    this.onSuggestionsFetchRequested = utils.debounce(
      this.onSuggestionsFetchRequested,
      100
    )
  }

  componentDidMount = () => {
    document.addEventListener('mousedown', this.handleClickOutside)
  }

  componentWillUnmount = () => {
    document.removeEventListener('mousedown', this.handleClickOutside)
  }

  handleClickOutside = (event) => {
    if (
      this.searchWrapperRef.current &&
      !this.searchWrapperRef.current.contains(event.target)
    ) {
      this.setState({ showList: false })
    }
  }

  handleOnImpersonateButtonClick = (event) => {
    this.setState({ isProcessing: true })
    this.props.impersonateUser(this.state.selectedUser).then(() => {
      window.location.href = '/appLauncher'
    })
  }

  onSuggestionsFetchRequested = ({ value }) => {
    this.setState({ isProcessing: true, currentPage: 0, showList: true })
    this.props.searchUsers(value, 0).then((result) => {
      this.setState({
        isProcessing: false,
        users: result.users,
        hasMore: result.pagination.hasMore
      })
    })
  }

  handleOnUserSelect = (user) => {
    const formattedValue = `${user.first_name} ${user.last_name} (${user.email})`
    this.setState({
      selectedUser: user,
      inputValue: formattedValue,
      showList: false
    })
  }

  handleOnLoadMore = (e) => {
    // Stop propagation to prevent the list from closing when clicking "Load More"
    e.stopPropagation()

    const nextPage = this.state.currentPage + 1
    this.setState({ isProcessing: true })

    this.props.searchUsers(this.state.inputValue, nextPage).then((result) => {
      // When sorting by first_name, the backend seems to return duplicated
      // entry on page 2 onwards. The first entry of page>=2 will always be the same
      // as the last entry of the previous page.
      // As a workaround, we filter out duplicate users by creating a map of existing user IDs
      const existingUserIds = new Set(this.state.users.map(user => user.id))
      const newUniqueUsers = result.users.filter(user => !existingUserIds.has(user.id))

      this.setState({
        isProcessing: false,
        users: [...this.state.users, ...newUniqueUsers],
        hasMore: result.pagination.hasMore,
        currentPage: nextPage
      })
    })
  }

  getSuggestionValue = (suggestion) => {
    return `${suggestion.first_name} ${suggestion.last_name} (${suggestion.email})`
  }

  renderSuggestion = (suggestion) => {
    return <div>{this.getSuggestionValue(suggestion)}</div>
  }

  onInputValueChange = (event, { newValue, method }) => {
    this.setState({ inputValue: newValue })
  }

  handleInputChange = (value) => {
    this.setState({
      inputValue: value,
      selectedUser: null,
      showList: value.length > 0
    })

    if (value.length > 0) {
      this.onSuggestionsFetchRequested({ value })
    } else {
      this.setState({ users: [], hasMore: false })
    }
  }

  handleInputFocus = () => {
    if (this.state.inputValue.length > 0) {
      this.setState({ showList: true })
    }
  }

  render () {
    if (this.state.redirectTo) {
      return <Redirect to={this.state.redirectTo} />
    }

    return (
      <Box display='flex' justifyContent='center'>
        <div>
          <Typography variant='h5'>{i18n.impersonateUser.header}</Typography>
          <Box className={this.classes.container}>
            {/* We need to use div instead of Box because ref doesn't seems to work with Swx components */}
            <div className={this.classes.searchWrapper} ref={this.searchWrapperRef}>
              <Textfield
                className={this.classes.searchInput}
                placeholder={i18n.impersonateUser.input.placeholder}
                value={this.state.inputValue}
                onChange={this.handleInputChange}
                onFocus={this.handleInputFocus}
                color='primary'
              />

              {this.state.showList && this.state.inputValue.length > 0 && (
                <UserList
                  classes={this.classes}
                  users={this.state.users}
                  onUserSelect={this.handleOnUserSelect}
                  selectedUser={this.state.selectedUser}
                  isLoading={this.state.isProcessing}
                  hasMore={this.state.hasMore}
                  onLoadMore={this.handleOnLoadMore}
                />
              )}
            </div>
            <Box>
              <Button
                onClick={this.handleOnImpersonateButtonClick}
                disabled={!this.state.selectedUser}
                className={this.classes.impersonateUserButton}
              >
                {i18n.impersonateUser.input.button}
              </Button>
            </Box>
          </Box>
        </div>
      </Box>
    )
  }
}

function mapStateToProps (state) {
  return {}
}

const ImpersonateUserComponent = withStyles(styles)(ImpersonateUser)

export default connect(mapStateToProps, { searchUsers, impersonateUser })(
  ImpersonateUserComponent
)
